import type {ReservationSource} from "@hosttools/core/constant";
import {useMemo} from "react";

import {buildChannel} from "../../models/channel";
import {memoWithType} from "../../react/utils/memoWithType";
import ChannelLogo from "../ChannelLogo";
import SelectGeneric from "../SelectGeneric";

type AllChannels = ReservationSource | "all";

type ChannelOption<T extends AllChannels> = {
    value: T;
    label: string;
};

export type Props<T extends AllChannels> = {
    channel?: T;
    channels: T[];
    textColor?: string;
    hasAllOption?: boolean;
    onChangeChannel: (channel: T) => void;
};

function SelectChannel<T extends AllChannels>({
    channel,
    channels,
    textColor,
    hasAllOption,
    onChangeChannel,
    ...selectProps
}: Props<T>) {
    const channelOptions: ChannelOption<T>[] = useMemo(
        () => [
            ...(hasAllOption ? [{value: "all" as T, label: "All"}] : []),
            ...channels.map(elem => {
                const channel = elem !== "all" ? buildChannel(elem) : undefined;
                return {
                    Icon: elem !== "all" ? <ChannelLogo channel={elem} /> : undefined,
                    value: elem,
                    label: channel?.label ?? "All"
                };
            })
        ],
        [hasAllOption, channels]
    );

    return (
        <SelectGeneric
            isSearchable={false}
            options={channelOptions}
            placeholder="Select channel"
            testID="select-channel"
            value={channel}
            onChange={onChangeChannel}
            {...selectProps}
        />
    );
}

export default memoWithType(SelectChannel);
